import { getAuthorizationHeader } from './talent-token';

type TError = {
  error: string;
  message: string;
  path: string;
  status: number;
  timestamp: number;
};

type TFetchParams = Parameters<typeof fetch>;
type TFetcherOptions = TFetchParams[1] & {
  skipAuth?: boolean;
  responseType?: 'json' | 'text';
};

export async function fetcher<TData = object | string>(
  url: TFetchParams[0],
  fetchOptions?: TFetcherOptions,
) {
  try {
    const {
      skipAuth,
      responseType = 'json',
      headers: customHeaders,
      ...requestOptions
    } = fetchOptions || {};
    const authHeader = skipAuth ? undefined : getAuthorizationHeader();
    const isFormData = requestOptions.body instanceof FormData;

    const response = await fetch(url, {
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(authHeader || {}),
        ...(customHeaders || {}),
      },
      body: requestOptions.body,
      ...requestOptions,
    });

    if (!response.ok) {
      let errMsg: string;
      try {
        const errorData: TError = await response.json();
        errMsg = `${errorData.error} ${errorData.status}: ${errorData.message}`;
      } catch {
        errMsg = `HTTP ${response.status}: ${await response.text().catch(() => 'Unknown error')}`;
      }
      throw new Error(errMsg);
    }
    const text = await response.text();
    const isEmpty = text.trim().length === 0;

    if (isEmpty) {
      return response.status;
    }

    if (responseType === 'text') {
      return text as TData;
    }

    const data: TData = JSON.parse(text);
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Error fetching data: ${error.message}`);
    } else {
      throw new Error(`Error fetching data: Unknown error`);
    }
  }
}
